import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import type {Finding, FindingSourceKind, SourceReference} from './types'

/** Display order for severity levels (most severe first) */
export const SEVERITY_ORDER: RuleSeverity[] = [
  'failure',
  'critical',
  'high',
  'error',
  'medium',
  'warning',
  'low',
  'note',
  'nit',
  'notice',
  'none',
]

const getSeveritySortIndex = (severity: RuleSeverity | undefined): number => {
  if (!severity) {
    return SEVERITY_ORDER.length
  }

  const index = SEVERITY_ORDER.indexOf(severity)
  return index === -1 ? SEVERITY_ORDER.length : index
}

const SEVERITY_MAP: Record<string, RuleSeverity> = {
  critical: 'critical',
  high: 'high',
  moderate: 'medium',
  low: 'low',
}

const SOURCE_LABELS: Record<FindingSourceKind, string> = {
  check_suite: 'Actions',
  pull_request_backfill: 'Actions',
  check_run: 'Actions',
  pr_review_comment: 'Review comment',
  pr_timeline_comment: 'Pull request comment',
  ccr_review_comment: 'Copilot',
  ci_analysis: 'CI Analysis',
  ci_failure_analysis: 'CI Failure Analysis',
  code_scanning: 'Code scanning',
}

/** Author logins that implicitly contribute additional source labels to a finding.
 *  These authors are excluded from the per-author filter since they are represented by their implied label. */
export const AUTHOR_IMPLIED_SOURCE_LABELS: ReadonlyMap<string, string> = new Map([
  ['copilot', 'Copilot'],
  ['github-actions[bot]', 'Actions'],
])

const CHECK_SOURCE_TYPES = new Set(['check_suite', 'pull_request_backfill', 'check_run'])

const SOURCE_CONTEXT_LABELS: Record<FindingSourceKind, string> = {
  check_suite: 'View source run',
  pull_request_backfill: 'View source run',
  check_run: 'View source run',
  pr_review_comment: 'View source comment',
  pr_timeline_comment: 'View source comment',
  ccr_review_comment: 'View source comment',
  ci_analysis: 'View CI Analysis source',
  ci_failure_analysis: 'View CI Failure Analysis',
  code_scanning: 'View Code scanning run',
}

const DISPLAY_PATH_LEADING_SEGMENTS = 2
const DISPLAY_PATH_MIN_SUFFIX_SEGMENTS = 2
const DISPLAY_PATH_FULL_SEGMENT_LIMIT = 4

export const getFindingSeverity = (finding: Finding): RuleSeverity | undefined => {
  return SEVERITY_MAP[finding.severity]
}

const getSourceLabel = (source: string): string => SOURCE_LABELS[source as FindingSourceKind] ?? source

const getRenderableSourceLabel = (reference: SourceReference): string => {
  const toolName = reference.toolName?.trim()
  if (toolName && CHECK_SOURCE_TYPES.has(reference.source)) return toolName

  return getSourceLabel(reference.source)
}

const getSourceIdentityKey = (reference: SourceReference, login?: string): string => {
  const source = reference.source
  const url = reference.url?.trim()
  if (url) return `${source}|url|${url}`

  const entityId = reference.entityId?.trim()
  if (entityId) return `${source}|entity|${entityId}`

  const toolName = reference.toolName?.trim()
  if (toolName) return `${source}|tool|${toolName.toLowerCase()}`

  return `${source}|author|${login?.toLowerCase() ?? ''}`
}

export const getFindingSourceLabels = (finding: Finding): string[] => {
  const labels: string[] = []
  const seen = new Set<string>()

  for (const reference of finding.sourceReferences ?? []) {
    const label = getSourceLabel(reference.source)
    if (!seen.has(label)) {
      seen.add(label)
      labels.push(label)
    }

    const login = reference.authorLogin?.trim().toLowerCase()
    if (login) {
      const impliedLabel = AUTHOR_IMPLIED_SOURCE_LABELS.get(login)
      if (impliedLabel && !seen.has(impliedLabel)) {
        seen.add(impliedLabel)
        labels.push(impliedLabel)
      }
    }
  }

  return labels
}

export interface SourceAuthor {
  login: string
  avatarUrl?: string
  isBot?: boolean
}

export interface FindingSource {
  key: string
  label: string
  url?: string
  author?: SourceAuthor
}

export interface RenderableFileLocation {
  displayLocation: string
  fullLocation: string
  url: string
}

export const getFindingSources = (finding: Finding): FindingSource[] => {
  const sources: FindingSource[] = []
  const indexByKey = new Map<string, number>()

  for (const reference of finding.sourceReferences ?? []) {
    const login = reference.authorLogin?.trim() || undefined
    const avatarUrl = reference.authorAvatarUrl || undefined
    const author = login ? {login, avatarUrl, isBot: reference.authorIsBot} : undefined
    const toolName = reference.toolName?.trim()
    const hasRenderableToolName = Boolean(toolName && CHECK_SOURCE_TYPES.has(reference.source))

    if (!reference.url && !author && !hasRenderableToolName) continue

    const label = getRenderableSourceLabel(reference)
    const key = getSourceIdentityKey(reference, login)
    const existingIndex = indexByKey.get(key)
    if (existingIndex !== undefined) {
      const existing = sources[existingIndex]
      if (existing && existing.label === getSourceLabel(reference.source) && label !== existing.label) {
        sources[existingIndex] = {...existing, label}
      }
      continue
    }
    indexByKey.set(key, sources.length)

    sources.push({
      key,
      label,
      url: reference.url,
      author,
    })
  }

  return sources
}

export const getFindingSourceAuthors = (finding: Finding): SourceAuthor[] => {
  const authors: SourceAuthor[] = []
  const seenLogins = new Set<string>()

  for (const reference of finding.sourceReferences ?? []) {
    const login = reference.authorLogin?.trim()
    if (!login) continue

    const key = login.toLowerCase()
    if (seenLogins.has(key)) continue

    seenLogins.add(key)
    authors.push({login, avatarUrl: reference.authorAvatarUrl || undefined, isBot: reference.authorIsBot})
  }

  return authors
}

const normalizeFilePath = (filePath: string): string =>
  filePath.replace(/\\/g, '/').split('/').filter(Boolean).join('/')

const getPathSegments = (normalizedFilePath: string): string[] =>
  normalizedFilePath ? normalizedFilePath.split('/') : []

const renderDisplayFilePath = (segments: string[], suffixSegments: number): string => {
  if (segments.length <= DISPLAY_PATH_FULL_SEGMENT_LIMIT) return segments.join('/')

  const suffix = segments.slice(-suffixSegments)
  if (DISPLAY_PATH_LEADING_SEGMENTS + suffix.length >= segments.length) return segments.join('/')

  return `${segments.slice(0, DISPLAY_PATH_LEADING_SEGMENTS).join('/')}/.../${suffix.join('/')}`
}

export const getDisplayFilePath = (filePath: string): string => {
  const normalized = normalizeFilePath(filePath)
  const segments = getPathSegments(normalized)
  return renderDisplayFilePath(segments, DISPLAY_PATH_MIN_SUFFIX_SEGMENTS)
}

export const getDisplayFilePaths = (filePaths: string[]): Map<string, string> => {
  const entries = filePaths.map(filePath => {
    const normalized = normalizeFilePath(filePath)

    return {
      original: filePath,
      normalized,
      segments: getPathSegments(normalized),
      suffixSegments: DISPLAY_PATH_MIN_SUFFIX_SEGMENTS,
    }
  })

  while (true) {
    const groups = new Map<string, typeof entries>()
    for (const entry of entries) {
      const displayPath = renderDisplayFilePath(entry.segments, entry.suffixSegments)
      groups.set(displayPath, [...(groups.get(displayPath) ?? []), entry])
    }

    const collisions = [...groups.values()].filter(group => new Set(group.map(entry => entry.normalized)).size > 1)
    if (collisions.length === 0) break

    for (const group of collisions) {
      for (const entry of group) {
        entry.suffixSegments += 1
      }
    }
  }

  return new Map(entries.map(entry => [entry.original, renderDisplayFilePath(entry.segments, entry.suffixSegments)]))
}

const getFindingLocationSuffix = (finding: Finding): string => {
  if (finding.startLine <= 0) return ''

  if (finding.startLine === finding.endLine) {
    let suffix = `:${finding.startLine}`
    if (finding.startColumn > 0) {
      suffix += `:${finding.startColumn}`
    }
    return suffix
  }

  return `:${finding.startLine}-${finding.endLine}`
}

export const getFindingFullFileLocation = (finding: Finding): string | null => {
  if (!finding.filePath) return null

  return `${finding.filePath}${getFindingLocationSuffix(finding)}`
}

export const getFindingFileLocation = (finding: Finding): string | null => {
  if (!finding.filePath) return null

  return `${getDisplayFilePath(finding.filePath)}${getFindingLocationSuffix(finding)}`
}

export const getFindingFileLocations = (
  findings: Finding[],
  displayFilePaths = getDisplayFilePaths(findings.map(finding => finding.filePath)),
): Map<Finding, string | null> => {
  return new Map(
    findings.map(finding => {
      if (!finding.filePath) return [finding, null]

      return [
        finding,
        `${displayFilePaths.get(finding.filePath) ?? getDisplayFilePath(finding.filePath)}${getFindingLocationSuffix(finding)}`,
      ]
    }),
  )
}

export const getFindingRenderableFileLocation = (
  finding: Finding,
  displayFilePath = getDisplayFilePath(finding.filePath),
): RenderableFileLocation | null => {
  if (!finding.filePath || !finding.fileUrl) return null

  const suffix = getFindingLocationSuffix(finding)

  return {
    displayLocation: `${displayFilePath}${suffix}`,
    fullLocation: `${finding.filePath}${suffix}`,
    url: finding.fileUrl,
  }
}

const isTrustedSourceLinkUrl = (url: string): boolean => {
  if (url.startsWith('/') && !url.startsWith('//')) return true
  if (!url.startsWith('https://')) return false

  try {
    const currentOrigin = typeof window === 'undefined' ? 'https://github.com' : window.location.origin
    const parsed = new URL(url, currentOrigin)
    return parsed.protocol === 'https:' && parsed.origin === currentOrigin
  } catch {
    return false
  }
}

const getSourceContextLabel = (source: string): string =>
  SOURCE_CONTEXT_LABELS[source as FindingSourceKind] ?? `View ${getSourceLabel(source)} source`

export interface SourceContextLink {
  source: string
  url: string
  label: string
}

/**
 * Builds contextual "View source" links from a finding's sourceReferences.
 *
 * Only trusted urls are included. URLs already represented by the descriptive
 * source links (getFindingSources) are skipped so the same destination is not
 * linked twice; the descriptive link is preferred.
 */
export const getFindingSourceContextLinks = (
  finding: Finding,
  excludeUrls: Set<string> = new Set(
    getFindingSources(finding)
      .map(source => source.url)
      .filter((url): url is string => Boolean(url)),
  ),
): SourceContextLink[] => {
  const links: SourceContextLink[] = []
  const seenUrls = new Set<string>()

  for (const reference of finding.sourceReferences ?? []) {
    const url = reference.url
    if (!url || !isTrustedSourceLinkUrl(url) || excludeUrls.has(url) || seenUrls.has(url)) continue

    seenUrls.add(url)
    links.push({
      source: reference.source,
      url,
      label: reference.linkLabel || getSourceContextLabel(reference.source),
    })
  }

  return links
}

export const getFindingTitle = (finding: Finding): string => {
  const msg = finding.groupTitle
  if (msg.length <= 100) return msg
  const truncated = msg.slice(0, 100)
  const lastSpace = truncated.lastIndexOf(' ')
  return lastSpace > 0 ? `${truncated.slice(0, lastSpace)}…` : `${truncated}…`
}

export const sortFindingsBySeverity = (findings: Finding[]): Finding[] => {
  return [...findings].sort((a, b) => {
    const indexA = getSeveritySortIndex(getFindingSeverity(a))
    const indexB = getSeveritySortIndex(getFindingSeverity(b))
    return indexA - indexB
  })
}

export const partitionFindings = (findings: Finding[]): {open: Finding[]; ignored: Finding[]} => {
  const open: Finding[] = []
  const ignored: Finding[] = []
  for (const f of findings) {
    if (f.resolvedAt) ignored.push(f)
    else open.push(f)
  }
  return {open: sortFindingsBySeverity(open), ignored: sortFindingsBySeverity(ignored)}
}

const HIGH_ATTENTION_SEVERITIES: ReadonlySet<RuleSeverity> = new Set(['critical', 'high'])

export const isNeedsAttention = (finding: Finding): boolean => {
  const severity = getFindingSeverity(finding)
  return !!severity && HIGH_ATTENTION_SEVERITIES.has(severity)
}
